<template>
    <router-view
        :form="form"
        :options="options"
        :licence="licence"
        :products="products"
        :submitting="submitting"
        :updating="!! data.active.vps"
        :selected-product="selectedProduct"
    ></router-view>
</template>

<script>
export default {
    props: {
        data: Object,
        order: Object,
    },

    inject: [
        'calculateTotals',
        'updateItemPromise',
    ],

    data() {
        return {
            submitting: false,
            form: {
                product_id: null,
                os_id: null,
                ip_id: null,
                hostname: null,
                licence_id: null,
                // management_id: null,
            },
            selectedProduct: null,
        };
    },

    mounted() {
        this.reset();

        window.bus.$on('fill-active-order-item', this.fillActiveOrderItem);

        this.fillActiveOrderItem();
    },

    provide() {
        return {
            reset: this.reset,
            total: this.total,
            submit: this.submit,
            setProductId: this.setProductId,
            productPrice: this.productPrice,
            specifications: this.specifications,
            optionDisplayName: this.optionDisplayName,
            productSpecifications: this.productSpecifications,
        };
    },

    computed: {
        options() {
            return { ...this.data.config.products.vps, os: this.data.config.products.vps.os.sort((a, b) => a.name.localeCompare(b.name)) };
        },
        products() {
            return this.data.vps;
        },
        formAttributes() {
            return this.data.active.vps
                ? { method: 'put', action: `/api/orders/${this.order.slug}/vps/${this.data.active.vps}` }
                : { method: 'post', action: `/api/orders/${this.order.slug}/vps/items` };
        },
        product() {
            return this.products.find(p => p.id == this.form.product_id);
        },
        os() {
            return this.options.os.find(o => o.id == this.form.os_id);
        },
        ip() {
            return this.options.ip.find(o => o.id == this.form.ip_id);
        },
        licence() {
            return this.options.addons.licences.find(o => o.id == this.form.licence_id);
        },
        // management() {
        //     return this.options.addons.management.find(o => o.id == this.form.management_id);
        // },
        preparedForm() {
            return {
                package_id: this.form.product_id,
                os: this.form.os_id,
                ips: this.form.ip_id,
                hostname: this.form.hostname,
                licence: this.form.licence_id ? [this.form.licence_id] : [],
                // management: this.form.management_id,
            };
        }
    },

    methods: {
        reset() {
            this.submitting = false;

            Object.keys(this.form).forEach(key => this.form[key] = null);

            const sortByPrice = (a, b) => {
                return a.price[this.userCurrency] - b.price[this.userCurrency];
            };

            const defaultProduct = this.products.find(p => p.name == this.$route.query.product_id) || this.products[0];
            this.selectedProduct = defaultProduct;

            const defaultOs = [...this.options.os].sort(sortByPrice)[0];
            const defaultIp = [...this.options.ip].sort(sortByPrice)[0];
            // const defaultManagement = [...this.options.addons.management].sort(sortByPrice)[0];

            this.form.product_id = defaultProduct ? defaultProduct.id : (this.products[0] ? this.products[0].id : null);
            this.form.os_id = defaultOs ? defaultOs.id : null;
            this.form.ip_id = defaultIp ? defaultIp.id : null;
            // this.form.management_id = defaultManagement ? defaultManagement.id : null;
        },

        fillActiveOrderItem() {

            const activeOrderItem = this.order.items
                .filter(item => item.type === 'vps')
                .find(item => item.slug === this.data.active.vps);

            if (! activeOrderItem) {
                return;
            }

            this.form.product_id = activeOrderItem.id;
            this.form.os_id = activeOrderItem.os.id;
            this.form.ip_id = activeOrderItem.ips.id;
            this.form.hostname = activeOrderItem.hostname
            // this.form.management_id = activeOrderItem.addons.management.id;
            this.form.licence_id = activeOrderItem.licence ? activeOrderItem.licence.id : null;
        },

        submit() {
            this.submitting = true;

            (this.data.active.vps
                ? this.updateItemPromise('vps', this.data.active.vps, this.preparedForm)
                : window.axios.post(`/api/orders/${this.order.slug}/vps/items`, this.preparedForm).then(response => this.order.items.push(response.data))
            ).then(response => {
                this.reset();
                this.calculateTotals();
                this.$router.push('/order/view');
            }).catch(error => {
                window.console.log(error.response);
            }).finally(() => {
                this.submitting = false;
            });
        },

        setProductId(productId) {
            this.form.product_id = productId;
            this.selectedProduct = this.products.find(p => p.id == productId);
        },

        total() {
            let price = parseFloat(this.productPrice(
                this.product ? this.product : this.products[0]
            ).replace(/[^0-9.-]+/g, ''));

            price += parseFloat(this.os ? this.os.price[this.userCurrency] : 0);
            price += parseFloat(this.ip ? this.ip.price[this.userCurrency] : 0);
            price += parseFloat(this.licence ? this.licence.price[this.userCurrency] : 0);
            // price += parseFloat(this.management ? this.management.price[this.userCurrency] : 0);

            return this.formatCurrency(price);
        },

        productPrice(product) {
            return this.formatCurrency(
                product.price[this.userCurrency]
            );
        },

        formatCurrency(amount) {
            const formatter = new Intl.NumberFormat('en-AU', {
                currency: 'AUD',
                style: 'currency',
                minimumFractionDigits: 2,
            });

            return formatter.format(amount);
        },

        optionDisplayName(option) {
            if(! option) return 'None';
            const price = option.price[this.userCurrency];
            return price > 0 ? `${option.name} - ${this.formatCurrency(price)}` : option.name;
        },

        specifications() {
            return this.productSpecifications(
                this.product ? this.product : this.products[0]
            );
        },

        productSpecifications(product) {
            // CPU Cores: 2<br /> \nMemory: 2 GB<br /> \nDisk Space: 50GB<br /> \nBandwidth: 1TB
            let parts = product.description.split('\n')
                .map(part => part.split(':')[1])
                .map(part => part.split('<')[0]);

            return {
                // Product specs:
                cpu: parts[0] ? parts[0] : 'n/a',
                memory: parts[1] ? parts[1] : 'n/a',
                storage: parts[2] ? parts[2] : 'n/a',
                data: parts[3] ? parts[3] : 'n/a',

                // Option specs:
                os: this.optionDisplayName(this.os),
                ip: this.optionDisplayName(this.ip),
                licences: this.optionDisplayName(this.licence),
                // management: this.optionDisplayName(this.management),
            };
        },
    },
}
</script>
